import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API, useOrg } from "@/App";
import axios from "axios";
import { BarChart3, Users, Package, Calculator, DollarSign, Download, FolderTree } from "lucide-react";

const ReportsPage = () => {
  const { currentOrg } = useOrg();
  const [activeTab, setActiveTab] = useState("sales");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchReport = async (type) => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      const params = {};
      if (dateRange.start) params.start_date = dateRange.start;
      if (dateRange.end) params.end_date = dateRange.end;

      const endpoints = {
        sales: '/reports/sales-summary',
        customers: '/reports/customer-report',
        items: '/reports/item-report',
        categories: '/reports/category-report',
        taxes: '/reports/tax-report',
        payments: '/reports/payments-report'
      };

      const response = await axios.get(`${API}${endpoints[type]}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id },
        params
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(activeTab);
  }, [currentOrg, activeTab, dateRange]);

  const exportToCSV = (reportData, filename) => {
    if (!reportData || !Array.isArray(reportData)) return;
    
    const headers = Object.keys(reportData[0] || {});
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-outfit text-2xl font-medium text-zinc-900">Reports</h1>
        <p className="text-muted-foreground text-sm">Analyze your business performance</p>
      </div>

      {/* Date Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <Label>Start Date</Label>
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
            className="w-40"
          />
        </div>
        <div>
          <Label>End Date</Label>
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
            className="w-40"
          />
        </div>
        <Button variant="outline" onClick={() => setDateRange({ start: "", end: "" })}>
          Clear
        </Button>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="sales" className="gap-2">
            <BarChart3 className="w-4 h-4" /> Sales
          </TabsTrigger>
          <TabsTrigger value="customers" className="gap-2">
            <Users className="w-4 h-4" /> Customers
          </TabsTrigger>
          <TabsTrigger value="items" className="gap-2">
            <Package className="w-4 h-4" /> Items
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2">
            <FolderTree className="w-4 h-4" /> Categories
          </TabsTrigger>
          <TabsTrigger value="taxes" className="gap-2">
            <Calculator className="w-4 h-4" /> Taxes
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <DollarSign className="w-4 h-4" /> Payments
          </TabsTrigger>
        </TabsList>

        {/* Sales Summary */}
        <TabsContent value="sales" className="mt-6">
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-lg font-medium">Sales Summary</h2>
            </div>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : data ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-50 p-4">
                    <p className="text-sm text-muted-foreground">Total Invoices</p>
                    <p className="text-2xl font-outfit font-medium">{data.total_invoices}</p>
                  </div>
                  <div className="bg-emerald-50 p-4">
                    <p className="text-sm text-emerald-600">Total Revenue</p>
                    <p className="text-2xl font-outfit font-medium text-emerald-700">${data.total_revenue?.toLocaleString()}</p>
                  </div>
                  <div className="bg-blue-50 p-4">
                    <p className="text-sm text-blue-600">Paid</p>
                    <p className="text-2xl font-outfit font-medium text-blue-700">${data.total_paid?.toLocaleString()}</p>
                  </div>
                  <div className="bg-amber-50 p-4">
                    <p className="text-sm text-amber-600">Outstanding</p>
                    <p className="text-2xl font-outfit font-medium text-amber-700">${data.total_outstanding?.toLocaleString()}</p>
                  </div>
                </div>
                {data.status_breakdown && (
                  <div>
                    <h3 className="font-medium mb-3">Status Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {Object.entries(data.status_breakdown).map(([status, count]) => (
                        <div key={status} className="p-3 bg-zinc-50 text-center">
                          <p className="text-lg font-medium">{count}</p>
                          <p className="text-xs text-muted-foreground capitalize">{status.replace('_', ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </TabsContent>

        {/* Customer Report */}
        <TabsContent value="customers" className="mt-6">
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-zinc-200">
              <h2 className="font-outfit font-medium">Customer Report</h2>
              {data && Array.isArray(data) && (
                <Button variant="outline" size="sm" onClick={() => exportToCSV(data, 'customer-report')} className="gap-2">
                  <Download className="w-4 h-4" /> Export
                </Button>
              )}
            </div>
            {loading ? (
              <div className="p-8 text-center">Loading...</div>
            ) : data && Array.isArray(data) && data.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Customer</th>
                      <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Invoices</th>
                      <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Total Sales</th>
                      <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Paid</th>
                      <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, i) => (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="py-3 px-4 font-medium">{row.customer_name}</td>
                        <td className="py-3 px-4 text-right">{row.total_invoices}</td>
                        <td className="py-3 px-4 text-right">${row.total_sales?.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-emerald-600">${row.total_paid?.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-amber-600">${row.total_outstanding?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">No data available</div>
            )}
          </div>
        </TabsContent>

        {/* Item Report */}
        <TabsContent value="items" className="mt-6">
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-zinc-200">
              <h2 className="font-outfit font-medium">Item Sales Report</h2>
              {data && Array.isArray(data) && (
                <Button variant="outline" size="sm" onClick={() => exportToCSV(data, 'item-report')} className="gap-2">
                  <Download className="w-4 h-4" /> Export
                </Button>
              )}
            </div>
            {loading ? (
              <div className="p-8 text-center">Loading...</div>
            ) : data && Array.isArray(data) && data.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Item</th>
                      <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Quantity Sold</th>
                      <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Total Sales</th>
                      <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Tax Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, i) => (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="py-3 px-4 font-medium">{row.item_name || 'Custom Item'}</td>
                        <td className="py-3 px-4 text-right">{row.total_quantity?.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right">${row.total_sales?.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right">${row.total_tax?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">No data available</div>
            )}
          </div>
        </TabsContent>

        {/* Category Report */}
        <TabsContent value="categories" className="mt-6">
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-lg font-medium">Category Sales Report</h2>
              {data?.categories && (
                <Button variant="outline" size="sm" onClick={() => exportToCSV(data.categories.map(c => ({
                  category: c.category, quantity: c.total_quantity, sales: c.total_sales, tax: c.total_tax, items: c.item_count
                })), 'category-report')} className="gap-2">
                  <Download className="w-4 h-4" /> Export
                </Button>
              )}
            </div>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : data?.categories ? (
              <div className="space-y-6">
                <div className="bg-primary/10 p-6 text-center">
                  <p className="text-sm text-primary">Total Sales by Category</p>
                  <p className="text-3xl font-outfit font-medium text-primary">${data.total_sales?.toLocaleString()}</p>
                </div>
                {data.categories.length > 0 ? (
                  <div className="space-y-4">
                    {data.categories.map((cat, i) => (
                      <div key={i} className="border border-zinc-200 overflow-hidden" data-testid={`category-report-${i}`}>
                        <div className="flex items-center justify-between p-4 bg-zinc-50">
                          <div className="flex items-center gap-3">
                            <FolderTree className="w-4 h-4 text-primary" />
                            <div>
                              <p className="font-medium">{cat.category}</p>
                              <p className="text-xs text-muted-foreground">{cat.item_count} line item{cat.item_count !== 1 ? 's' : ''} sold</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-primary">${cat.total_sales?.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">Qty: {cat.total_quantity}</p>
                          </div>
                        </div>
                        {cat.subcategories?.length > 0 && (
                          <div className="divide-y divide-zinc-100">
                            {cat.subcategories.map((sub, j) => (
                              <div key={j} className="flex items-center justify-between px-4 py-2.5 pl-12">
                                <p className="text-sm text-zinc-600">{sub.subcategory}</p>
                                <div className="text-right text-sm">
                                  <span className="font-medium">${sub.total_sales?.toFixed(2)}</span>
                                  <span className="text-muted-foreground ml-3">Qty: {sub.total_quantity}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground">No category sales data available</div>
                )}
              </div>
            ) : null}
          </div>
        </TabsContent>

        {/* Tax Report */}
        <TabsContent value="taxes" className="mt-6">
          <div className="bg-white border border-zinc-200 p-6">
            <h2 className="font-outfit text-lg font-medium mb-6">Tax Report</h2>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : data ? (
              <div className="space-y-6">
                <div className="bg-primary/10 p-6 text-center">
                  <p className="text-sm text-primary">Total Tax Collected</p>
                  <p className="text-3xl font-outfit font-medium text-primary">${data.total_tax_collected?.toFixed(2)}</p>
                </div>
                {data.tax_breakdown && data.tax_breakdown.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 border-b border-zinc-200">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Tax Rule</th>
                          <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Type</th>
                          <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Rate</th>
                          <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Invoices</th>
                          <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Taxable Amount</th>
                          <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Tax Collected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.tax_breakdown.map((row, i) => (
                          <tr key={i} className="border-b border-zinc-100">
                            <td className="py-3 px-4 font-medium">{row.tax_name}</td>
                            <td className="py-3 px-4 capitalize">{row.tax_type}</td>
                            <td className="py-3 px-4 text-right">
                              {row.tax_type === 'percentage' ? `${row.tax_rate}%` : `$${row.tax_rate?.toFixed(2)}`}
                            </td>
                            <td className="py-3 px-4 text-right">{row.invoice_count}</td>
                            <td className="py-3 px-4 text-right">${row.total_taxable_amount?.toFixed(2)}</td>
                            <td className="py-3 px-4 text-right font-medium text-primary">${row.total_tax_collected?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </TabsContent>

        {/* Payments Report */}
        <TabsContent value="payments" className="mt-6">
          <div className="bg-white border border-zinc-200 p-6">
            <h2 className="font-outfit text-lg font-medium mb-6">Payments Report</h2>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : data ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50 p-6 text-center">
                    <p className="text-sm text-emerald-600">Total Received</p>
                    <p className="text-3xl font-outfit font-medium text-emerald-700">${data.total_received?.toFixed(2)}</p>
                  </div>
                  <div className="bg-zinc-50 p-6 text-center">
                    <p className="text-sm text-muted-foreground">Payment Count</p>
                    <p className="text-3xl font-outfit font-medium">{data.payment_count}</p>
                  </div>
                </div>
                {data.method_breakdown && data.method_breakdown.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3">By Payment Method</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {data.method_breakdown.map((row, i) => (
                        <div key={i} className="p-4 bg-zinc-50">
                          <p className="text-lg font-medium">${row.total_amount?.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground capitalize">{row.payment_method?.replace('_', ' ')} ({row.count})</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
